/**
 * NotificationRow — single inbox entry.
 *
 * Visual rules:
 *   - Unread = subtle category-accent left-border + bold title
 *   - Read = muted, no border
 *   - Click = navigate to `url` AND mark read
 *   - Swipe-left on mobile = reveal dismiss
 *   - Long-press = mark read (no UI for now)
 *
 * Grouping (group_key) collapsing happens one level up in the drawer —
 * this row just renders one entry.
 */
import { useState, useRef } from 'react';
import { Trash, ArrowSquareOut, CheckCircle } from '@phosphor-icons/react';
import * as Phosphor from '@phosphor-icons/react';

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString();
};

const NotificationRow = ({ item, categoryMeta, onOpen, onRead, onDismiss }) => {
  const meta = categoryMeta || { label: 'System', icon: 'Gear', accent: '#71717A' };
  const Icon = Phosphor[meta.icon] || Phosphor.Gear;
  const [swipeX, setSwipeX] = useState(0);
  const startRef = useRef(null);
  const accent = meta.accent;
  const isUnread = !item.read;
  const isHigh = item.priority === 'high';

  const onTouchStart = (e) => { startRef.current = e.touches[0].clientX; };
  const onTouchMove = (e) => {
    if (startRef.current == null) return;
    const dx = e.touches[0].clientX - startRef.current;
    if (dx < 0 && dx > -120) setSwipeX(dx);
  };
  const onTouchEnd = () => {
    if (swipeX < -60) {
      onDismiss?.(item.id);
    }
    setSwipeX(0);
    startRef.current = null;
  };

  const handleClick = () => {
    if (isUnread) onRead?.(item.id);
    onOpen?.(item);
  };

  return (
    <li
      className="relative overflow-hidden rounded-xl transition-all"
      data-testid={`notif-row-${item.id}`}
    >
      {/* Swipe-reveal background */}
      <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none"
        style={{ width: 120, background: 'linear-gradient(90deg, transparent, rgba(244,63,94,0.18))' }}>
        <Trash size={16} weight="fill" style={{ color: '#F43F5E' }} />
      </div>

      <div
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); } }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        className="relative w-full flex items-start gap-3 px-3 py-3 text-left transition-transform cursor-pointer focus:outline-none"
        style={{
          transform: `translateX(${swipeX}px)`,
          background: isUnread
            ? `linear-gradient(90deg, ${accent}16 0%, var(--cs-bg-surface) 60%)`
            : 'var(--cs-bg-surface)',
          borderLeft: `3px solid ${isUnread ? accent : 'transparent'}`,
        }}
        data-testid={`notif-row-btn-${item.id}`}
      >
        <div
          className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center"
          style={{ background: `${accent}22`, border: `1px solid ${accent}33` }}
        >
          <Icon size={16} weight={isUnread ? 'fill' : 'regular'} style={{ color: accent }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-0.5">
            <span
              className="text-[10px] font-bold uppercase tracking-wider"
              style={{ color: accent }}
            >
              {meta.label}
            </span>
            {isHigh && (
              <span className="text-[9px] font-black px-1.5 py-0.5 rounded uppercase"
                style={{ background: 'rgba(244,63,94,0.18)', color: '#F43F5E' }}>
                High
              </span>
            )}
            <span className="text-[10px] ml-auto tabular-nums"
              style={{ color: 'var(--cs-text-dim)' }}>{timeAgo(item.created_at)}</span>
          </div>
          <div className={`text-[13px] leading-snug ${isUnread ? 'font-semibold' : 'font-medium'}`}
            style={{ color: 'var(--cs-text)' }}>
            {item.title}
          </div>
          {item.body && (
            <div className="text-[11.5px] leading-snug mt-0.5 line-clamp-2"
              style={{ color: 'var(--cs-text-muted)' }}>
              {item.body}
            </div>
          )}
          <div className="flex items-center gap-2 mt-1.5">
            {item.url && item.url !== '/' && (
              <span className="text-[10px] font-semibold inline-flex items-center gap-1"
                style={{ color: accent }}>
                <ArrowSquareOut size={11} weight="bold" /> Go
              </span>
            )}
            {isUnread && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onRead?.(item.id); }}
                className="text-[10px] font-semibold inline-flex items-center gap-1 ml-auto"
                style={{ color: 'var(--cs-text-muted)' }}
                data-testid={`notif-row-read-${item.id}`}
              >
                <CheckCircle size={11} weight="bold" /> Mark read
              </button>
            )}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDismiss?.(item.id); }}
              className="text-[10px] font-semibold inline-flex items-center gap-1 ml-auto"
              style={{ color: 'var(--cs-text-dim)' }}
              data-testid={`notif-row-dismiss-${item.id}`}
            >
              <Trash size={11} weight="bold" />
            </button>
          </div>
        </div>
      </div>
    </li>
  );
};

export default NotificationRow;
